import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, Input } from '@angular/core';
import { CHAT_STATUS_PRESETS, ChatStatusPreset } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';

/** One icon per preset — used in the dropdown rows, and as the compact trigger's own glyph. */
const PRESET_ICONS: Record<string, string> = {
  online: 'pi-circle-fill',
  dnd: 'pi-ban',
  away: 'pi-moon',
};

/**
 * Status switcher.
 *
 * The backend only ever persists and broadcasts two states — `online` and `dnd` (`offline` is a
 * consequence of having no socket, never something a user picks). "Away" buckets down to `dnd`
 * underneath: colleagues still only ever see the plain green/light-red dot, but your own trigger
 * keeps its own yellow — the richer label and colour are kept in this browser's storage rather
 * than sent to the server. See {@link ChatStore.setStatusPreset}.
 */
@Component({
  selector: 'app-chat-status-menu',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="chat-status">
      <button
        type="button"
        class="chat-status__trigger"
        [class.chat-status__trigger--compact]="compact"
        [title]="compact ? store.myStatusPreset().label : null"
        (click)="open = !open"
        [attr.aria-expanded]="open"
        [attr.aria-label]="compact ? 'Change status — currently ' + store.myStatusPreset().label : null">
        <i class="pi chat-status__trigger-icon is-{{ store.myStatusPreset().id }}" [ngClass]="iconFor(store.myStatusPreset())" *ngIf="compact"></i>
        <ng-container *ngIf="!compact">
          <span class="chat-status__dot" [ngClass]="'is-' + store.myStatusPreset().id"></span>
          <span class="chat-status__label">{{ store.myStatusPreset().label }}</span>
          <i class="pi pi-angle-down"></i>
        </ng-container>
      </button>

      <div class="chat-status__menu" [class.chat-status__menu--right]="align === 'right'" *ngIf="open">
        <button type="button" class="chat-status__option" *ngFor="let preset of presets" (click)="choose(preset)">
          <i class="pi chat-status__icon is-{{ preset.id }}" [ngClass]="iconFor(preset)"></i>
          <span>
            <strong>{{ preset.label }}</strong>
            <small>{{ preset.description }}</small>
          </span>
          <i class="pi pi-check chat-status__check" *ngIf="store.myStatusPreset().id === preset.id"></i>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .chat-status {
        position: relative;
      }

      .chat-status__trigger {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        background: transparent;
        padding: 4px 6px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 12px;
        color: var(--searchIcon);

        &:hover {
          background: var(--gray);
        }

        i {
          font-size: 10px;
        }
      }

      /* Matches the header's other icon buttons (chat-icon-button(30px) in _chat-shared.scss) so
         it sits flush among them instead of standing out as a differently-shaped control. */
      .chat-status__trigger--compact {
        width: 30px;
        height: 30px;
        padding: 0;
        justify-content: center;
        border-radius: 50%;
      }

      /* The preset's own glyph rather than a bare dot — legible at 30px next to the header's other
         icon buttons, and it names the preset (ban, moon…) instead of just its bucket. Keyed by
         preset id rather than bucket: Away and Do Not Disturb both bucket to "dnd" for the server,
         but still need their own colour here — yellow and light red are the whole point. */
      .chat-status__trigger-icon {
        font-size: 15px;

        &.is-online {
          color: var(--green);
        }

        &.is-dnd {
          color: var(--dangerLight);
        }

        &.is-away {
          color: var(--yellow);
        }
      }

      .chat-status__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex: 0 0 auto;

        &.is-online {
          background: var(--green);
        }

        &.is-dnd {
          background: var(--dangerLight);
        }

        &.is-away {
          background: var(--yellow);
        }

        &.is-offline {
          background: var(--lightDarkGray);
        }
      }

      .chat-status__menu {
        position: absolute;
        z-index: 30;
        top: calc(100% + 4px);
        left: 0;
        width: 264px;
        max-height: 360px;
        overflow-y: auto;
        background: var(--white);
        border: 1px solid var(--lightGray);
        border-radius: 10px;
        box-shadow: 0 8px 28px rgba(26, 47, 69, 0.14);
        padding: 6px;
      }

      /* A trigger flush against a panel's right edge (the conversation header's toolbar) would
         push the menu off-screen anchored from its left edge, so it opens from the right instead —
         the same anchoring the header's own "more actions" menu uses (chat-conv__menu). */
      .chat-status__menu--right {
        left: auto;
        right: 0;
      }

      .chat-status__option {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        width: 100%;
        border: none;
        background: transparent;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        text-align: left;

        &:hover {
          background: var(--lightGrayBG);
        }

        strong {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--black);
        }

        small {
          display: block;
          font-size: 11px;
          color: var(--searchIcon);
        }
      }

      .chat-status__icon {
        margin-top: 3px;
        font-size: 13px;
        flex: 0 0 auto;

        &.is-online {
          color: var(--green);
        }

        &.is-dnd {
          color: var(--dangerLight);
        }

        &.is-away {
          color: var(--yellow);
        }
      }

      .chat-status__check {
        margin-left: auto;
        color: var(--primary);
        font-size: 12px;
      }
    `,
  ],
})
export class ChatStatusMenuComponent {
  readonly store = inject(ChatStore);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  readonly presets: ChatStatusPreset[] = CHAT_STATUS_PRESETS;

  /** Icon-only trigger — no label, no chevron — for header toolbars with no room for text. */
  @Input() compact = false;
  /** Which side the dropdown opens from. `'right'` for a trigger sitting at a panel's right edge. */
  @Input() align: 'left' | 'right' = 'left';

  open = false;

  iconFor(preset: ChatStatusPreset): string {
    return PRESET_ICONS[preset.id] ?? 'pi-circle';
  }

  choose(preset: ChatStatusPreset): void {
    this.store.setStatusPreset(preset.id);
    this.open = false;
  }

  /** Click-away, so the menu never strands itself open behind another panel. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }
}
